import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Mail, Phone, MapPin, Trash2, UserPlus, Globe, CheckCircle2, Plus } from "lucide-react";
import { toast } from "sonner";
import { titleCase } from "@/lib/format";
import { ManualLeadDialog } from "@/components/ManualLeadDialog";

export default function Leads() {
    const [leads, setLeads] = useState([]);
    const [filter, setFilter] = useState("new");
    const [loading, setLoading] = useState(true);
    const [manualOpen, setManualOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = filter === "all" ? {} : { status: filter };
            const { data } = await api.get("/leads", { params });
            setLeads(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load leads");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const convert = async (lead) => {
        if (!window.confirm(`Convert "${lead.name}" into a real customer? They'll be enrolled in the quarterly billing cycle.`)) return;
        try {
            const { data } = await api.post(`/leads/${lead.id}/convert`);
            toast.success(`Converted — customer id ${data.customer_id.substring(0, 8)}…`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Convert failed");
        }
    };

    const dismiss = async (lead) => {
        if (!window.confirm(`Dismiss "${lead.name}" — spam, duplicate, or out-of-area?`)) return;
        try {
            await api.post(`/leads/${lead.id}/dismiss`);
            toast.success("Dismissed");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Dismiss failed");
        }
    };

    const newCount = leads.filter((l) => l.status === "new").length;

    return (
        <div className="space-y-6" data-testid="leads-page">
            <PageHeader
                title="Signup leads"
                subtitle="New customers submitted via the WordPress website widget. Convert to onboard them."
                actions={
                    <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => setManualOpen(true)}
                                data-testid="leads-add-manual-btn">
                            <Plus className="w-3.5 h-3.5 mr-1" />Add lead
                        </Button>
                        <Select value={filter} onValueChange={setFilter}>
                            <SelectTrigger className="w-40" data-testid="leads-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="converted">Converted</SelectItem>
                                <SelectItem value="dismissed">Dismissed</SelectItem>
                                <SelectItem value="all">All</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                }
            />
            <ManualLeadDialog open={manualOpen} onOpenChange={setManualOpen}
                              onCreated={load} />

            {filter === "new" && newCount > 0 && (
                <div className="text-sm p-3 bg-primary/5 border border-primary/20 rounded-md flex items-center gap-2">
                    <Globe className="w-4 h-4 text-primary" />
                    <span><span className="font-semibold">{newCount}</span> new signup{newCount === 1 ? "" : "s"} waiting for review</span>
                </div>
            )}

            {loading && (
                <div className="text-center py-16 text-sm text-muted-foreground">Loading leads…</div>
            )}

            {!loading && leads.length === 0 && (
                <Card>
                    <CardContent className="p-12 text-center">
                        <Globe className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
                        <div className="text-sm text-muted-foreground">
                            {filter === "new" ? "No new signups yet. The office will see them here as soon as the widget is embedded on your WordPress site." : "No leads match this filter."}
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {leads.map((l) => (
                    <Card key={l.id} data-testid={`lead-${l.id}`}>
                        <CardContent className="p-4 space-y-3">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <div className="font-heading text-lg font-bold truncate">{l.name}</div>
                                        <StatusBadge status={l.status} />
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                        Received {new Date(l.created_at).toLocaleString()}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-1.5 text-xs">
                                <div className="flex items-center gap-2">
                                    <Mail className="w-3 h-3 text-muted-foreground" />
                                    <a href={`mailto:${l.email}`} className="hover:underline">{l.email}</a>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Phone className="w-3 h-3 text-muted-foreground" />
                                    <a href={`tel:${l.phone}`} className="hover:underline">{l.phone}</a>
                                </div>
                                <div className="flex items-start gap-2">
                                    <MapPin className="w-3 h-3 text-muted-foreground mt-0.5" />
                                    <span>
                                        {l.address?.line1}{l.address?.line2 ? `, ${l.address.line2}` : ""}<br />
                                        {l.address?.city}, {l.address?.state} {l.address?.zip}
                                    </span>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-3 text-xs pt-2 border-t border-border">
                                <span><span className="text-muted-foreground">Carts:</span> <span className="font-medium">{l.cart_quantity}× {l.cart_size}</span></span>
                                <span><span className="text-muted-foreground">Preferred day:</span> <span className="font-medium">{l.preferred_day ? titleCase(l.preferred_day) : "No preference"}</span></span>
                            </div>

                            {l.notes && (
                                <div className="text-xs bg-secondary/40 rounded p-2 italic">"{l.notes}"</div>
                            )}

                            {l.status === "new" && (
                                <div className="grid grid-cols-2 gap-2 pt-1">
                                    <Button variant="outline" size="sm" onClick={() => dismiss(l)}
                                            data-testid={`lead-dismiss-${l.id}`}>
                                        <Trash2 className="w-4 h-4 mr-1" />Dismiss
                                    </Button>
                                    <Button size="sm" onClick={() => convert(l)}
                                            data-testid={`lead-convert-${l.id}`}>
                                        <UserPlus className="w-4 h-4 mr-1" />Convert
                                    </Button>
                                </div>
                            )}
                            {l.status === "converted" && l.converted_customer_id && (
                                <div className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3" />
                                    Converted — customer id {l.converted_customer_id.substring(0, 8)}…
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        new: { label: "NEW", tone: "bg-primary/10 text-primary border-primary/20" },
        converted: { label: "CONVERTED", tone: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
        dismissed: { label: "DISMISSED", tone: "bg-muted text-muted-foreground border-border" },
    };
    const c = map[status] || map.new;
    return (
        <Badge variant="outline" className={`text-[9px] tracking-wider ${c.tone}`}>
            {c.label}
        </Badge>
    );
}
